import { showToast } from '../toast';
import { nanoid } from 'nanoid';
import { debounce } from 'lodash-es';
import { ImageCompressor } from '../utils/image-compressor';

// Constants
const CONFIG_CACHE_KEY = 'image_config';
const CONFIG_CACHE_VERSION = '1.0.0';
const CONFIG_CACHE_DURATION = 1200000; // 20 minutes
const MAX_ERRORS = 1;
const ERROR_TIMEOUT = 3000; // 3 seconds

// Memoized mime types
const MIME_TYPES = {
    'jpeg': 'image/jpeg',
    'jpg': 'image/jpeg',
    'png': 'image/png'
};

export default function imageUploader({ type, modelId = null }) {
    return {
        // State
        files: [],
        loading: true,
        config: null,
        errors: [],
        existingImages: [], // Existing images from the model
        _errorTimeout: null, // For error cleanup
        _formContainer: null, // Cache form container reference
        _errorId: 0, // For generating unique error keys
        _compressor: null, // Image compressor instance

        // Initialize the component
        init() {
            this.loadConfig();
            if (modelId) {
                this.loadExistingImages();
            }

            // Initialize compressor with config
            this._compressor = new ImageCompressor({
                maxWidth: 1280,
                quality: 0.9
            });

            // Setup error cleanup
            const cleanupErrors = debounce(() => {
                if (this.errors.length > MAX_ERRORS) {
                    this.errors = this.errors.slice(-MAX_ERRORS);
                }
                if (this._errorTimeout) {
                    clearTimeout(this._errorTimeout);
                }
                this._errorTimeout = setTimeout(() => {
                    this.errors = [];
                }, ERROR_TIMEOUT);
            }, 100);

            this.$watch('errors', cleanupErrors);

            // Cleanup function
            const cleanup = () => {
                this.files.forEach(file => {
                    if (file?.preview) {
                        URL.revokeObjectURL(file.preview);
                    }
                });
                if (this._errorTimeout) {
                    clearTimeout(this._errorTimeout);
                }
            };

            // Set cleanup on destroy
            this.$cleanup = cleanup;
        },

        // Load configuration with versioning
        async loadConfig() {
            try {
                // Try to get config from cache first
                const cachedConfig = localStorage.getItem(CONFIG_CACHE_KEY);
                let configData;

                if (cachedConfig) {
                    try {
                        const { data, timestamp, version } = JSON.parse(cachedConfig);
                        // Check version and timestamp
                        if (version === CONFIG_CACHE_VERSION && 
                            Date.now() - timestamp < CONFIG_CACHE_DURATION) {
                            configData = data;
                        }
                    } catch (e) {
                        console.warn('Invalid cache format:', e);
                    }
                }

                // Load config if not cached
                if (!configData) {
                    const response = await fetch('/api/config/images');
                    if (!response.ok) {
                        throw new Error(`Failed to load config: ${response.status}`);
                    }
                    configData = await response.json();
                    localStorage.setItem(CONFIG_CACHE_KEY, JSON.stringify({
                        data: configData,
                        timestamp: Date.now(),
                        version: CONFIG_CACHE_VERSION
                    }));
                }
                
                // Validate config
                const typeConfig = configData[type];
                if (!typeConfig || 
                    !typeConfig.allowed_types || 
                    !Array.isArray(typeConfig.allowed_types) || 
                    typeof typeConfig.max_count !== 'number' || 
                    typeof typeConfig.min_count !== 'number' || 
                    typeof typeConfig.max_size !== 'number' || 
                    !typeConfig.dimensions) {
                    throw new Error('Invalid configuration format');
                }
                this.config = typeConfig;

                // Update compressor with config settings
                if (this._compressor && typeConfig.optimization) {
                    this._compressor = new ImageCompressor({
                        maxWidth: typeConfig.optimization.max_width,
                        quality: typeConfig.optimization.quality / 100 // Convert percentage to decimal
                    });
                }
            } catch (error) {
                this.addError('Failed to load image configuration');
            } finally {
                this.loading = false;
            }
        },

        // Load existing images
        async loadExistingImages() {
            if (!modelId) return;
            
            try {
                const response = await fetch(`/api/${type}s/${modelId}/images`);
                
                if (!response.ok) {
                    const error = await response.json();
                    throw new Error(error.message || 'Failed to load images');
                }
                
                const data = await response.json();
                
                if (!data.images) {
                    throw new Error('Invalid response format');
                }
                
                // Filter out marked_for_deletion images and map the rest
                this.existingImages = data.images
                    .filter(image => !image.marked_for_deletion)
                    .map(image => ({
                        ...image,
                        deleting: false
                    }));

            } catch (error) {
                this.addError('Failed to load existing images');
            }
        },

        // Process and compress file
        async processFile(file) {
            try {
                // Validate file first
                const error = await this.validateFile(file);
                if (error) {
                    this.addError(`${file.name}: ${error}`);
                    return null;
                }

                // Compress the image
                const compressedBlob = await this._compressor.compress(file);
                
                // Create a File from the Blob to maintain name
                const compressedFile = new File([compressedBlob], file.name, {
                    type: file.type,
                    lastModified: file.lastModified
                });

                // Create preview URL
                const preview = URL.createObjectURL(compressedBlob);

                return {
                    id: nanoid(),
                    file: compressedFile,
                    name: file.name,
                    preview,
                    originalSize: file.size,
                    compressedSize: compressedBlob.size,
                    error: null
                };
            } catch (error) {
                this.addError(`Failed to process ${file.name}`);
                return null;
            }
        },

        // Handle file input change
        async handleFileInput(event) {
            // Prevent file handling if config isn't loaded
            if (!this.config) {
                this.addError('Please wait for configuration to load');
                event.target.value = '';
                return;
            }

            const newFiles = Array.from(event.target.files || []);
            event.target.value = ''; // Clear input immediately
            
            // Check total file count
            const totalCount = this.files.length + newFiles.length;
            if (totalCount < this.config.min_count) {
                this.addError(`Minimum ${this.config.min_count} ${this.config.min_count === 1 ? 'image' : 'images'} required`);
                return;
            }
            if (totalCount > this.config.max_count) {
                this.addError(`Maximum ${this.config.max_count} ${this.config.max_count === 1 ? 'image' : 'images'} allowed`);
                return;
            }
            
            // Process and compress files in parallel
            const processedFiles = await Promise.all(newFiles.map(file => this.processFile(file)));
            const validFiles = processedFiles.filter(f => f !== null);
            
            // Add valid files to the files array
            this.files.push(...validFiles);

            // Get or create form container
            if (!this._formContainer) {
                const form = this.$el.closest('form');
                if (!form) {
                    throw new Error('Could not find parent listing form');
                }
                
                this._formContainer = form.querySelector('.image-inputs-container') || (() => {
                    const container = document.createElement('div');
                    container.className = 'image-inputs-container';
                    container.style.display = 'none';
                    form.appendChild(container);
                    return container;
                })();
            }
            
            // Batch DOM operations for file inputs
            const fragment = document.createDocumentFragment();
            validFiles.forEach(file => {
                const input = document.createElement('input');
                input.type = 'file';
                input.name = 'images[]';
                input.id = `image-${file.id}`;
                
                const dataTransfer = new DataTransfer();
                dataTransfer.items.add(file.file);
                input.files = dataTransfer.files;
                
                fragment.appendChild(input);
            });
            this._formContainer.appendChild(fragment);
            
            // Add optimized form submit handler
            const form = this._formContainer.closest('form');
            if (!form.dataset.hasSubmitHandler) {
                const handleSubmit = (e) => {
                    const hasFiles = this._formContainer.querySelectorAll('input[name="images[]"]').length > 0;
                    const hasDeletedImages = this.existingImages.some(img => img.marked_for_deletion);
                    
                    if (!hasFiles && !hasDeletedImages) {
                        e.preventDefault();
                        window.location.href = `/listings/${modelId}`;
                    }
                };
                form.addEventListener('submit', handleSubmit);
                form.dataset.hasSubmitHandler = 'true';
                
                // Cleanup handler on component destroy
                const existingCleanup = this.$cleanup;
                this.$cleanup = () => {
                    existingCleanup?.();
                    form.removeEventListener('submit', handleSubmit);
                };
            }
        },

        // Remove file
        removeFile(fileId) {
            const file = this.files.find(f => f.id === fileId);
            
            if (file?.preview) {
                URL.revokeObjectURL(file.preview);
            }
            
            this.files = this.files.filter(f => f.id !== fileId);

            if (this._formContainer) {
                const input = this._formContainer.querySelector(`#image-${fileId}`);
                input?.remove();
            }
        },

        // Delete existing image with validation
        async deleteImage(imageId) {
            if (!modelId) {
                this.addError('Cannot delete image: No model ID provided');
                return;
            }
            
            if (!this.config) {
                this.addError('Cannot delete image: Configuration not loaded');
                return;
            }

            const image = this.existingImages.find(img => img.id === imageId);
            
            if (!image || image.deleting) {
                this.addError('Cannot delete image: Already being processed');
                return;
            }

            // Check if deleting would violate minimum image count
            const currentCount = this.existingImages.length;
            const pendingUploads = this.files.length;
            if (currentCount - 1 + pendingUploads < this.config.min_count) {
                this.addError(`Cannot delete image: Minimum ${this.config.min_count} ${this.config.min_count === 1 ? 'image is' : 'images are'} required`);
                return;
            }

            try {
                image.deleting = true;
                const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
                const url = `/api/${type}s/${modelId}/images/${imageId}/mark-deletion`;

                const response = await fetch(url, {
                    method: 'PATCH',
                    headers: {
                        'X-CSRF-TOKEN': csrfToken,
                        'Accept': 'application/json',
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        pending_uploads_count: this.files.length
                    })
                });

                if (!response.ok) {
                    const error = await response.json();
                    if (response.status === 422) {
                        // Handle validation errors more gracefully
                        const message = error.message || 
                            (error.errors?.images?.[0]) || 
                            'Validation failed';
                        this.addError(`Cannot delete image: ${message}`);
                        return;
                    }
                    throw new Error(error.message || 'Failed to delete image');
                }
                
                await this.loadExistingImages();

            } catch (error) {
                showToast(error.message || 'Failed to delete image', 'error');
            } finally {
                if (image) {
                    image.deleting = false;
                }
            }
        },

        // Validate file
        validateFile(file) {
            if (!this.config) return 'Configuration not loaded';

            const allowedTypes = this.config.allowed_types.map(ext => MIME_TYPES[ext]);
            if (!allowedTypes.includes(file.type)) {
                return `Invalid file type. Only ${this.config.allowed_types.join(', ').toUpperCase()} are allowed.`;
            }

            // Check file size
            const maxSizeInBytes = this.config.max_size * 1024;
            if (file.size > maxSizeInBytes) {
                return `File too large. Maximum size is ${this.config.max_size / 1024}MB`;
            }

            // Check dimensions
            return new Promise((resolve) => {
                const img = new Image();
                img.onload = () => {
                    URL.revokeObjectURL(img.src);
                    
                    if (img.width < this.config.dimensions.min_width || img.height < this.config.dimensions.min_height) {
                        resolve(`Image too small. Minimum dimensions are ${this.config.dimensions.min_width}x${this.config.dimensions.min_height}px`);
                    }
                    
                    if (img.width > this.config.dimensions.max_width || img.height > this.config.dimensions.max_height) {
                        resolve(`Image too large. Maximum dimensions are ${this.config.dimensions.max_width}x${this.config.dimensions.max_height}px`);
                    }
                    
                    resolve(null);
                };
                
                img.onerror = () => {
                    URL.revokeObjectURL(img.src);
                    resolve('Invalid image file');
                };
                
                img.src = URL.createObjectURL(file);
            });
        },

        // Format file size
        formatFileSize(bytes) {
            if (bytes === 0) return '0 B';
            const k = 1024;
            const sizes = ['B', 'KB', 'MB', 'GB'];
            const i = Math.floor(Math.log(bytes) / Math.log(k));
            return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
        },

        // Add error with unique ID
        addError(message) {
            const error = {
                id: `error_${++this._errorId}`,
                message,
                timestamp: Date.now()
            };
            
            // Maintain maximum number of errors
            if (this.errors.length >= MAX_ERRORS) {
                this.errors.shift(); // Remove oldest error
            }
            this.errors.push(error);

            // Auto-remove error after timeout
            setTimeout(() => {
                this.errors = this.errors.filter(e => e.id !== error.id);
            }, ERROR_TIMEOUT);
        }
    };
}
